import { DRAWER_SEARCH_PARAM_ITEM_ID } from '@/constants/drawer-search';
import { FALLBACK_TAG_GROUPS, FALLBACK_TAG_TERMS } from '@/lib/item-filter-fallbacks';
import type { FilterTagGroup, FilterTagTerm, ItemFilters } from '@/types';
import type { ItemFilterSearch, ItemListSearch } from '@/lib/validation';

export const FALLBACK_TYPE_OPTIONS = [
  { slug: 'block', name: 'block' },
  { slug: 'section', name: 'section' },
  { slug: 'full-page', name: 'full-page' },
];

export function mergeSelectedOption<T extends { slug: string; name: string }>(
  options: T[],
  selected?: string,
): T[] {
  if (!selected) {
    return options;
  }

  if (options.some((option) => option.slug === selected)) {
    return options;
  }

  return [...options, { slug: selected, name: selected } as T];
}

export function parseMultiValueFilter(value?: string): string[] {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );
}

export function serializeMultiValueFilter(values: string[]): string | undefined {
  const normalized = Array.from(
    new Set(
      values
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ).sort();

  return normalized.length > 0 ? normalized.join(',') : undefined;
}

export function mergeSelectedTagGroups(
  groups: FilterTagGroup[],
  flatTags: FilterTagTerm[],
  selectedValues: string[],
): FilterTagGroup[] {
  if (selectedValues.length === 0) {
    return groups;
  }

  const knownSlugs = new Set(groups.flatMap((group) => group.terms.map((term) => term.slug)));
  const flatTagMap = new Map(flatTags.map((term) => [term.slug, term]));
  const missingTerms = selectedValues
    .filter((selectedValue) => !knownSlugs.has(selectedValue))
    .map((selectedValue) => flatTagMap.get(selectedValue) ?? {
      slug: selectedValue,
      name: selectedValue,
      group: 'selected',
      description: '',
      sortOrder: 9999,
      active: true,
      count: 0,
    });

  if (missingTerms.length === 0) {
    return groups;
  }

  return [
    ...groups,
    {
      slug: 'selected',
      name: 'Selected',
      description: 'Selected tags not present in the current filter payload.',
      sortOrder: 9999,
      terms: missingTerms,
    },
  ];
}

export function getLibraryFilterStatusMessage(
  filterGroups: ItemFilters | undefined,
  isLoading: boolean,
  isError: boolean,
): string | null {
  if (!filterGroups && isLoading) {
    return 'Loading filters. Showing fallback options first.';
  }

  if (!filterGroups && isError) {
    return "Couldn't load filters. Showing fallback options.";
  }

  return null;
}

export function normalizeLibraryFilterSearch(search: ItemFilterSearch): ItemFilterSearch {
  const query = search.query?.trim() ? search.query.trim() : undefined;
  const tags = search.tags?.trim() ? search.tags : undefined;

  return {
    query,
    type: search.type,
    tags,
    ui: tags ? undefined : search.ui,
  };
}

export function getFallbackTagGroups(): FilterTagGroup[] {
  return FALLBACK_TAG_GROUPS.map((group) => ({
    ...group,
    terms: group.terms.map((term) => ({ ...term })),
  }));
}

export function getFallbackTagTerms(): FilterTagTerm[] {
  return FALLBACK_TAG_TERMS.map((term) => ({ ...term }));
}

export function buildLibraryDetailSearch(search: ItemFilterSearch): ItemFilterSearch {
  return normalizeLibraryFilterSearch(search);
}

export function buildLibraryListSearch(search: ItemFilterSearch): ItemListSearch {
  return {
    ...buildLibraryDetailSearch(search),
    [DRAWER_SEARCH_PARAM_ITEM_ID]: undefined,
  };
}

export function buildLibraryDrawerOpenSearch(
  search: ItemFilterSearch,
  itemId: number,
): ItemListSearch {
  return {
    ...buildLibraryDetailSearch(search),
    [DRAWER_SEARCH_PARAM_ITEM_ID]: itemId,
  };
}

export function buildLibraryDrawerCloseSearch(search: ItemFilterSearch): ItemListSearch {
  return {
    ...buildLibraryDetailSearch(search),
    [DRAWER_SEARCH_PARAM_ITEM_ID]: undefined,
  };
}
